import { useMemo, useState, useEffect } from "react";
import { Plus, Trash2, Printer, RotateCcw, FileText, Lock, LogOut } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { Seo } from "../components/Seo";
import { SITE } from "../lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const LS_KEY = "dw_estimate_v1";
const AUTH_KEY = "dw_estimate_auth_v1";

const emptyLine = () => ({ id: crypto.randomUUID(), description: "", qty: 1, unit_price: 0 });

const defaultForm = () => ({
  estimate_no: `EST-${new Date().getFullYear()}-${String(Math.floor(Math.random() * 9000) + 1000)}`,
  date: new Date().toISOString().slice(0, 10),
  valid_days: 14,
  // customer
  customer_name: "",
  customer_phone: "",
  customer_email: "",
  customer_address: "",
  // vehicle
  vehicle: "",
  vin: "",
  mileage: "",
  license_plate: "",
  // line items
  parts: [emptyLine()],
  labor: [{ id: crypto.randomUUID(), description: "Labor", hours: 1, rate: 125 }],
  // adjustments
  tax_rate_pct: 0,
  discount: 0,
  deposit: 0,
  // notes
  notes: "",
});

const currency = (n) =>
  Number(n || 0).toLocaleString(undefined, { style: "currency", currency: "USD" });

const inputCls =
  "w-full bg-[#0A0A0A] border border-white/10 focus:border-[#FF3B30] outline-none text-white text-sm px-3 py-2 transition-colors placeholder:text-white/30";

const Field = ({ label, children, className = "" }) => (
  <label className={`block ${className}`}>
    <span className="font-mono-tech text-[10px] tracking-[0.2em] uppercase text-white/55">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);

export default function Estimate() {
  const [authed, setAuthed] = useState(() => {
    if (typeof window === "undefined") return false;
    try { return localStorage.getItem(AUTH_KEY) === "1"; } catch { return false; }
  });

  if (!authed) return <PinGate onUnlock={() => setAuthed(true)} />;

  return <EstimateBuilder onLock={() => {
    try { localStorage.removeItem(AUTH_KEY); } catch {}
    setAuthed(false);
  }} />;
}

const PinGate = ({ onUnlock }) => {
  const [pin, setPin] = useState("");
  const [state, setState] = useState({ status: "idle", error: null });

  const submit = async (e) => {
    e.preventDefault();
    if (state.status === "submitting" || !pin) return;
    setState({ status: "submitting", error: null });
    try {
      const res = await fetch(`${API}/estimate-auth`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pin }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Invalid PIN");
      }
      try { localStorage.setItem(AUTH_KEY, "1"); } catch {}
      onUnlock();
    } catch (err) {
      setState({ status: "error", error: err.message });
      setPin("");
    }
  };

  return (
    <div data-testid="estimate-pin-gate" className="min-h-[70vh] flex items-center justify-center px-6">
      <form onSubmit={submit} className="w-full max-w-sm bg-[#0E0E0E] border border-white/10 p-8">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 grid place-items-center bg-[#FF3B30]/15 text-[#FF3B30]">
            <Lock className="w-4 h-4" />
          </div>
          <div>
            <div className="font-mono-tech text-[11px] text-[#FF3B30] tracking-[0.3em] uppercase">// staff only</div>
            <div className="font-display font-black uppercase tracking-tight text-xl">Enter PIN</div>
          </div>
        </div>
        <p className="mt-5 text-white/55 text-sm leading-relaxed">
          Internal tool. Customers don't need this — hit the browser back button and use the Book, Build or Contact pages instead.
        </p>
        <label className="block mt-6">
          <span className="font-mono-tech text-[10px] tracking-[0.25em] uppercase text-white/55">PIN</span>
          <input
            data-testid="estimate-pin-input"
            type="password"
            inputMode="numeric"
            autoFocus
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            className="mt-2 w-full bg-[#0A0A0A] border border-white/10 focus:border-[#FF3B30] outline-none text-white text-lg tracking-[0.5em] text-center px-3 py-3"
            placeholder="••••"
          />
        </label>
        {state.status === "error" && (
          <div data-testid="estimate-pin-error" className="mt-3 text-[#FF8A82] text-sm">{state.error}</div>
        )}
        <button
          data-testid="estimate-pin-submit"
          type="submit"
          disabled={state.status === "submitting" || !pin}
          className="mt-5 w-full bg-[#FF3B30] hover:bg-[#FF5722] disabled:opacity-60 text-white text-xs font-bold uppercase tracking-[0.25em] py-3 transition-colors"
        >
          {state.status === "submitting" ? "Checking…" : "Unlock"}
        </button>
      </form>
    </div>
  );
};

const EstimateBuilder = ({ onLock }) => {
  const [f, setF] = useState(() => {
    if (typeof window === "undefined") return defaultForm();
    try {
      const raw = localStorage.getItem(LS_KEY);
      return raw ? { ...defaultForm(), ...JSON.parse(raw) } : defaultForm();
    } catch { return defaultForm(); }
  });

  useEffect(() => {
    try { localStorage.setItem(LS_KEY, JSON.stringify(f)); } catch { /* ignore */ }
  }, [f]);

  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const setNum = (k) => (e) => setF((s) => ({ ...s, [k]: parseFloat(e.target.value) || 0 }));

  const updatePart = (id, k, v) =>
    setF((s) => ({ ...s, parts: s.parts.map((p) => (p.id === id ? { ...p, [k]: v } : p)) }));
  const updateLabor = (id, k, v) =>
    setF((s) => ({ ...s, labor: s.labor.map((p) => (p.id === id ? { ...p, [k]: v } : p)) }));

  const totals = useMemo(() => {
    const parts_total = f.parts.reduce((acc, p) => acc + (parseFloat(p.qty) || 0) * (parseFloat(p.unit_price) || 0), 0);
    const labor_total = f.labor.reduce((acc, l) => acc + (parseFloat(l.hours) || 0) * (parseFloat(l.rate) || 0), 0);
    const subtotal = parts_total + labor_total;
    const discount = parseFloat(f.discount) || 0;
    const taxable = Math.max(0, subtotal - discount);
    const tax = taxable * ((parseFloat(f.tax_rate_pct) || 0) / 100);
    const total = taxable + tax;
    const deposit = parseFloat(f.deposit) || 0;
    const balance_due = Math.max(0, total - deposit);
    return { parts_total, labor_total, subtotal, discount, tax, total, deposit, balance_due };
  }, [f]);

  const valid_until = useMemo(() => {
    try {
      const d = new Date(f.date);
      d.setDate(d.getDate() + (parseInt(f.valid_days, 10) || 0));
      return d.toISOString().slice(0, 10);
    } catch { return ""; }
  }, [f.date, f.valid_days]);

  const reset = () => {
    if (window.confirm("Clear this estimate?")) setF(defaultForm());
  };

  return (
    <div data-testid="page-estimate">
      <Seo
        path="/estimate"
        title="Estimate Builder — Printable Estimate with Terms & Hold Harmless"
        description="Build a printable customer estimate with itemized parts and labor, tax, deposit, terms & conditions, and a hold harmless agreement. Dirty Wrenches Automotive, Hermiston OR."
      />

      <div className="no-print">
        <PageHeader
          code="// 09"
          kicker="Estimate Builder"
          title={<>Build an<br />estimate.</>}
          subtitle="Fill it in, review the preview, print it (or save as PDF). Includes terms & conditions and hold harmless agreement. Data auto-saves in your browser."
          testId="estimate-header"
        />
      </div>

      <section className="px-6 md:px-12 lg:px-24 py-12 md:py-16 grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* -------- FORM (hidden on print) -------- */}
        <div className="no-print lg:col-span-5 space-y-6">
          <div className="bg-[#0E0E0E] border border-white/10 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="font-mono-tech text-[11px] text-[#FF3B30] tracking-[0.3em] uppercase">// meta</div>
              <div className="flex gap-2">
                <button
                  onClick={reset}
                  data-testid="estimate-reset"
                  className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-white/60 hover:text-white border border-white/15 hover:border-white/40 px-3 py-2"
                >
                  <RotateCcw className="w-3.5 h-3.5" /> Reset
                </button>
                <button
                  onClick={onLock}
                  data-testid="estimate-lock"
                  className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-white/60 hover:text-white border border-white/15 hover:border-white/40 px-3 py-2"
                >
                  <LogOut className="w-3.5 h-3.5" /> Lock
                </button>
                <button
                  onClick={() => window.print()}
                  data-testid="estimate-print"
                  className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-white bg-[#FF3B30] hover:bg-[#FF5722] px-3 py-2"
                >
                  <Printer className="w-3.5 h-3.5" /> Print / PDF
                </button>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Estimate #"><input data-testid="est-input-no" className={inputCls} value={f.estimate_no} onChange={set("estimate_no")} /></Field>
              <Field label="Date"><input type="date" className={inputCls} value={f.date} onChange={set("date")} /></Field>
              <Field label="Valid (days)"><input type="number" min="0" className={inputCls} value={f.valid_days} onChange={setNum("valid_days")} /></Field>
            </div>
          </div>

          <div className="bg-[#0E0E0E] border border-white/10 p-6 space-y-3">
            <div className="font-mono-tech text-[11px] text-[#FF3B30] tracking-[0.3em] uppercase">// customer</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Name"><input data-testid="est-input-customer" className={inputCls} value={f.customer_name} onChange={set("customer_name")} placeholder="First Last" /></Field>
              <Field label="Phone"><input className={inputCls} value={f.customer_phone} onChange={set("customer_phone")} placeholder="(541) 555-0100" /></Field>
              <Field label="Email"><input className={inputCls} value={f.customer_email} onChange={set("customer_email")} placeholder="you@example.com" /></Field>
              <Field label="Address"><input className={inputCls} value={f.customer_address} onChange={set("customer_address")} placeholder="Street, City, State ZIP" /></Field>
            </div>
          </div>

          <div className="bg-[#0E0E0E] border border-white/10 p-6 space-y-3">
            <div className="font-mono-tech text-[11px] text-[#FF3B30] tracking-[0.3em] uppercase">// vehicle</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Year / Make / Model / Trim"><input data-testid="est-input-vehicle" className={inputCls} value={f.vehicle} onChange={set("vehicle")} placeholder="2018 BMW M3 Competition" /></Field>
              <Field label="VIN"><input className={inputCls} value={f.vin} onChange={set("vin")} /></Field>
              <Field label="Mileage"><input className={inputCls} value={f.mileage} onChange={set("mileage")} /></Field>
              <Field label="Plate"><input className={inputCls} value={f.license_plate} onChange={set("license_plate")} /></Field>
            </div>
          </div>

          <div className="bg-[#0E0E0E] border border-white/10 p-6 space-y-3">
            <div className="flex items-center justify-between">
              <div className="font-mono-tech text-[11px] text-[#FF3B30] tracking-[0.3em] uppercase">// parts</div>
              <button
                onClick={() => setF((s) => ({ ...s, parts: [...s.parts, emptyLine()] }))}
                data-testid="est-add-part"
                className="text-xs font-bold uppercase tracking-[0.2em] text-white/70 hover:text-white inline-flex items-center gap-1"
              ><Plus className="w-3.5 h-3.5" /> Add</button>
            </div>
            {f.parts.map((p, i) => (
              <div key={p.id} className="grid grid-cols-12 gap-2 items-center">
                <input data-testid={`est-part-desc-${i}`} className={`${inputCls} col-span-7`} value={p.description} onChange={(e) => updatePart(p.id, "description", e.target.value)} placeholder="Part description" />
                <input type="number" min="0" step="0.5" className={`${inputCls} col-span-2`} value={p.qty} onChange={(e) => updatePart(p.id, "qty", e.target.value)} placeholder="Qty" />
                <input type="number" min="0" step="0.01" className={`${inputCls} col-span-2`} value={p.unit_price} onChange={(e) => updatePart(p.id, "unit_price", e.target.value)} placeholder="$" />
                <button
                  onClick={() => setF((s) => ({ ...s, parts: s.parts.filter((x) => x.id !== p.id) }))}
                  className="col-span-1 text-white/40 hover:text-[#FF3B30] p-2"
                  aria-label="Remove line"
                ><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>

          <div className="bg-[#0E0E0E] border border-white/10 p-6 space-y-3">
            <div className="flex items-center justify-between">
              <div className="font-mono-tech text-[11px] text-[#FF3B30] tracking-[0.3em] uppercase">// labor</div>
              <button
                onClick={() => setF((s) => ({ ...s, labor: [...s.labor, { id: crypto.randomUUID(), description: "Labor", hours: 1, rate: 125 }] }))}
                className="text-xs font-bold uppercase tracking-[0.2em] text-white/70 hover:text-white inline-flex items-center gap-1"
              ><Plus className="w-3.5 h-3.5" /> Add</button>
            </div>
            {f.labor.map((l, i) => (
              <div key={l.id} className="grid grid-cols-12 gap-2 items-center">
                <input data-testid={`est-labor-desc-${i}`} className={`${inputCls} col-span-6`} value={l.description} onChange={(e) => updateLabor(l.id, "description", e.target.value)} placeholder="Labor description" />
                <input type="number" min="0" step="0.25" className={`${inputCls} col-span-3`} value={l.hours} onChange={(e) => updateLabor(l.id, "hours", e.target.value)} placeholder="Hrs" />
                <input type="number" min="0" step="0.01" className={`${inputCls} col-span-2`} value={l.rate} onChange={(e) => updateLabor(l.id, "rate", e.target.value)} placeholder="$/hr" />
                <button
                  onClick={() => setF((s) => ({ ...s, labor: s.labor.filter((x) => x.id !== l.id) }))}
                  className="col-span-1 text-white/40 hover:text-[#FF3B30] p-2"
                  aria-label="Remove line"
                ><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>

          <div className="bg-[#0E0E0E] border border-white/10 p-6 grid grid-cols-3 gap-3">
            <Field label="Discount ($)"><input type="number" min="0" step="0.01" className={inputCls} value={f.discount} onChange={setNum("discount")} /></Field>
            <Field label="Tax rate (%)"><input type="number" min="0" step="0.01" className={inputCls} value={f.tax_rate_pct} onChange={setNum("tax_rate_pct")} /></Field>
            <Field label="Deposit ($)"><input type="number" min="0" step="0.01" className={inputCls} value={f.deposit} onChange={setNum("deposit")} /></Field>
          </div>

          <div className="bg-[#0E0E0E] border border-white/10 p-6">
            <Field label="Additional notes for customer">
              <textarea className={`${inputCls} min-h-[100px] resize-y`} value={f.notes} onChange={set("notes")} placeholder="Special instructions, parts lead time, etc." />
            </Field>
          </div>
        </div>

        {/* -------- PREVIEW / PRINT AREA -------- */}
        <div className="lg:col-span-7">
          <div className="no-print mb-3 font-mono-tech text-[11px] text-white/40 tracking-[0.3em] uppercase flex items-center gap-2">
            <FileText className="w-3.5 h-3.5" /> Live preview — print for customer copy
          </div>

          <article
            id="estimate-print"
            data-testid="estimate-print-area"
            className="bg-white text-neutral-900 shadow-xl p-8 md:p-10 print:p-6 print:shadow-none"
            style={{ fontFamily: 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif' }}
          >
            {/* Letterhead */}
            <header className="flex items-start justify-between border-b border-neutral-200 pb-5">
              <div className="flex items-center gap-3">
                <img src="/logo.jpg" alt="Dirty Wrenches" className="w-14 h-14 rounded-full object-cover" />
                <div>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 font-mono">Est. 2022 · Hermiston, OR</div>
                  <div className="text-xl font-black tracking-tight">{SITE.fullName.toUpperCase()}</div>
                </div>
              </div>
              <div className="text-right text-xs text-neutral-600 leading-relaxed">
                <div>{SITE.contact.address}</div>
                <div>{SITE.contact.phone} · {SITE.contact.email}</div>
                <div>https://dirty-wrenches.com</div>
              </div>
            </header>

            {/* Title + meta */}
            <div className="mt-6 flex items-start justify-between">
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#FF3B30] font-mono">Estimate</div>
                <div className="text-3xl font-black tracking-tight">{f.estimate_no || "EST-—"}</div>
              </div>
              <div className="text-right text-xs text-neutral-600">
                <div><span className="text-neutral-400 uppercase tracking-wider">Date</span> {f.date}</div>
                <div><span className="text-neutral-400 uppercase tracking-wider">Valid until</span> {valid_until}</div>
              </div>
            </div>

            {/* Customer + vehicle */}
            <div className="mt-6 grid grid-cols-2 gap-6 text-sm">
              <div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-neutral-400 font-mono mb-1">Bill to</div>
                <div className="font-bold">{f.customer_name || "—"}</div>
                <div className="text-neutral-600">{f.customer_phone}</div>
                <div className="text-neutral-600">{f.customer_email}</div>
                <div className="text-neutral-600">{f.customer_address}</div>
              </div>
              <div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-neutral-400 font-mono mb-1">Vehicle</div>
                <div className="font-bold">{f.vehicle || "—"}</div>
                {f.vin && <div className="text-neutral-600">VIN: {f.vin}</div>}
                {f.mileage && <div className="text-neutral-600">Mileage: {f.mileage}</div>}
                {f.license_plate && <div className="text-neutral-600">Plate: {f.license_plate}</div>}
              </div>
            </div>

            {/* Line items */}
            <table className="w-full mt-6 text-sm border-collapse">
              <thead>
                <tr className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 font-mono">
                  <th className="text-left py-2 border-b border-neutral-300">Description</th>
                  <th className="text-right py-2 border-b border-neutral-300 w-16">Qty/Hrs</th>
                  <th className="text-right py-2 border-b border-neutral-300 w-24">Rate</th>
                  <th className="text-right py-2 border-b border-neutral-300 w-24">Amount</th>
                </tr>
              </thead>
              <tbody>
                {f.parts.map((p) => (
                  <tr key={p.id} className="border-b border-neutral-100">
                    <td className="py-2">{p.description || <span className="text-neutral-300">Part —</span>}</td>
                    <td className="py-2 text-right">{p.qty || 0}</td>
                    <td className="py-2 text-right">{currency(p.unit_price)}</td>
                    <td className="py-2 text-right font-medium">{currency((parseFloat(p.qty) || 0) * (parseFloat(p.unit_price) || 0))}</td>
                  </tr>
                ))}
                {f.labor.map((l) => (
                  <tr key={l.id} className="border-b border-neutral-100">
                    <td className="py-2">{l.description || <span className="text-neutral-300">Labor —</span>}</td>
                    <td className="py-2 text-right">{l.hours || 0}</td>
                    <td className="py-2 text-right">{currency(l.rate)}/hr</td>
                    <td className="py-2 text-right font-medium">{currency((parseFloat(l.hours) || 0) * (parseFloat(l.rate) || 0))}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Totals */}
            <div className="mt-4 flex justify-end">
              <div className="w-full max-w-xs text-sm">
                <div className="flex justify-between py-1"><span className="text-neutral-500">Parts subtotal</span><span>{currency(totals.parts_total)}</span></div>
                <div className="flex justify-between py-1"><span className="text-neutral-500">Labor subtotal</span><span>{currency(totals.labor_total)}</span></div>
                <div className="flex justify-between py-1 border-t border-neutral-200"><span className="text-neutral-500">Subtotal</span><span>{currency(totals.subtotal)}</span></div>
                {totals.discount > 0 && <div className="flex justify-between py-1 text-emerald-700"><span>Discount</span><span>−{currency(totals.discount)}</span></div>}
                {totals.tax > 0 && <div className="flex justify-between py-1"><span className="text-neutral-500">Tax ({f.tax_rate_pct}%)</span><span>{currency(totals.tax)}</span></div>}
                <div className="flex justify-between py-2 border-t border-neutral-300 font-black text-base"><span>Total</span><span>{currency(totals.total)}</span></div>
                {totals.deposit > 0 && (
                  <>
                    <div className="flex justify-between py-1 text-neutral-600"><span>Deposit</span><span>−{currency(totals.deposit)}</span></div>
                    <div className="flex justify-between py-2 border-t border-neutral-200 font-bold text-base text-[#FF3B30]"><span>Balance due</span><span>{currency(totals.balance_due)}</span></div>
                  </>
                )}
              </div>
            </div>

            {f.notes && (
              <div className="mt-6 pt-4 border-t border-neutral-200 text-sm">
                <div className="text-[10px] tracking-[0.25em] uppercase text-neutral-400 font-mono mb-1">Notes</div>
                <div className="whitespace-pre-wrap text-neutral-700 leading-relaxed">{f.notes}</div>
              </div>
            )}

            {/* Warranty note */}
            <div className="mt-6 pt-4 border-t border-neutral-200 text-xs text-neutral-600">
              <span className="font-bold text-neutral-900">Warranty:</span> All work performed is backed by our {SITE.warranty.months}-month / {SITE.warranty.miles.toLocaleString()}-mile warranty. Warranty covers parts and labor of the specific repair performed; excludes wear items, misuse, and unrelated failures.
            </div>

            {/* Terms & Conditions */}
            <section className="mt-6 pt-4 border-t border-neutral-300">
              <h3 className="text-sm font-black tracking-tight uppercase">Terms &amp; Conditions</h3>
              <ol className="mt-2 list-decimal list-inside text-[11px] text-neutral-700 leading-relaxed space-y-1">
                <li>This is an <strong>estimate only</strong>. Final invoice may differ if additional issues are found during service. Any changes will be communicated and require customer approval before proceeding.</li>
                <li>Estimate is valid for the number of days shown on the estimate. Parts pricing and availability are subject to supplier changes after that date.</li>
                <li>A non-refundable deposit may be required for special-order parts.</li>
                <li>Payment is due upon completion of work. We accept cash, check, and major credit/debit cards. Vehicles may be held until payment is received (mechanic's lien).</li>
                <li>Diagnostic time may apply if additional inspection is required beyond the scope of this estimate.</li>
                <li>Old parts will be returned upon customer request (subject to core-charge exceptions).</li>
                <li>All work performed carries a 12-month / 12,000-mile warranty (whichever comes first) on parts and labor for the specific repair, excluding wear items and consequential damage.</li>
                <li>Storage fees may apply to vehicles left more than 72 hours after work is completed and customer has been notified.</li>
              </ol>
            </section>

            {/* Hold Harmless */}
            <section className="mt-6 pt-4 border-t border-neutral-300">
              <h3 className="text-sm font-black tracking-tight uppercase">Hold Harmless Agreement</h3>
              <p className="mt-2 text-[11px] text-neutral-700 leading-relaxed">
                Customer acknowledges that <strong>{SITE.fullName}</strong>, its owners, employees and agents (collectively, "the Shop") are not responsible for loss or damage to the vehicle, or articles left in the vehicle, in case of fire, theft, accident, vandalism, or any other cause beyond the Shop's reasonable control while the vehicle is on the Shop's premises or being test-driven for the purpose of diagnosis or repair. Customer authorizes the Shop and its employees to operate the vehicle on streets, highways, or elsewhere for the purpose of testing, inspection, or delivery at customer's risk. Customer acknowledges that modifications, tuning, performance work, and off-road / track use are performed at customer's own risk and may affect the vehicle's factory warranty, insurance, and street-legal status. Customer releases the Shop from any and all liability arising from such modifications or intended use.
              </p>
            </section>

            {/* Signatures */}
            <section className="mt-8 pt-4 border-t-2 border-neutral-800">
              <h3 className="text-sm font-black tracking-tight uppercase mb-4">Signatures</h3>
              <div className="grid grid-cols-2 gap-8 text-xs">
                <div>
                  <div className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 font-mono mb-6">Customer</div>
                  <div className="border-b border-neutral-800 h-12"></div>
                  <div className="mt-1 text-neutral-500 uppercase tracking-wider text-[10px]">Signature</div>
                  <div className="mt-6 grid grid-cols-2 gap-4">
                    <div>
                      <div className="border-b border-neutral-800 h-8"></div>
                      <div className="mt-1 text-neutral-500 uppercase tracking-wider text-[10px]">Printed name</div>
                      <div className="mt-1 text-neutral-800 text-[11px]">{f.customer_name || "—"}</div>
                    </div>
                    <div>
                      <div className="border-b border-neutral-800 h-8"></div>
                      <div className="mt-1 text-neutral-500 uppercase tracking-wider text-[10px]">Date</div>
                    </div>
                  </div>
                </div>
                <div>
                  <div className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 font-mono mb-6">Shop representative</div>
                  <div className="border-b border-neutral-800 h-12"></div>
                  <div className="mt-1 text-neutral-500 uppercase tracking-wider text-[10px]">Signature</div>
                  <div className="mt-6 grid grid-cols-2 gap-4">
                    <div>
                      <div className="border-b border-neutral-800 h-8"></div>
                      <div className="mt-1 text-neutral-500 uppercase tracking-wider text-[10px]">Printed name</div>
                      <div className="mt-1 text-neutral-800 text-[11px]">{SITE.fullName}</div>
                    </div>
                    <div>
                      <div className="border-b border-neutral-800 h-8"></div>
                      <div className="mt-1 text-neutral-500 uppercase tracking-wider text-[10px]">Date</div>
                    </div>
                  </div>
                </div>
              </div>
              <p className="mt-6 text-[10px] text-neutral-500 leading-relaxed">
                By signing above, the customer acknowledges they have read and agree to the Terms &amp; Conditions and the Hold Harmless Agreement on this estimate, and authorize {SITE.fullName} to perform the work described.
              </p>
            </section>

            <footer className="mt-8 pt-3 border-t border-neutral-200 text-center text-[10px] tracking-[0.25em] uppercase text-neutral-400 font-mono">
              {SITE.fullName} · {SITE.contact.phone} · Thank you for your business.
            </footer>
          </article>
        </div>
      </section>

      {/* Print CSS */}
      <style>{`
        @media print {
          @page { size: letter; margin: 0.5in; }
          html, body { background: #fff !important; }
          body * { visibility: hidden; }
          #estimate-print, #estimate-print * { visibility: visible; }
          #estimate-print { position: absolute; left: 0; top: 0; width: 100%; box-shadow: none !important; }
          .no-print { display: none !important; }
        }
      `}</style>
    </div>
  );
}
